import React, { useState, useEffect, useCallback } from 'react';
import ComplianceBadges from '../components/ComplianceBadges';

const API_BASE = process.env.REACT_APP_API_URL || '';

/* ---------- Types ---------- */
interface DemoPatient { id: string; name: string; site: string; arm: string; }
interface Deviation { field: string; expected: string; actual: string; severity: string; }
interface SDVResult { match_status: 'pass' | 'fail' | 'warning'; deviations: Deviation[]; }
interface TriggerResult {
  fhir_resource: Record<string, unknown>;
  sdv_result: SDVResult;
  ingestion_status: string;
  observation_id: string;
}
interface HistoryRow {
  time: string; patient: string; procedure: string; value: string;
  status: 'pass' | 'fail' | 'warning'; observation_id: string;
}

/* ---------- Visit schedules per protocol ---------- */
const PROTOCOL_VISITS: Record<string, string[]> = {
  'Sym004-09': [
    'V1 — Screening', 'V2 — Day 1 (Dose)', 'V3 — Day 2', 'V4 — Day 8',
    'V5 — Day 15', 'V6 — Day 22', 'V7 — Day 29', 'V8 — Day 36 (Dose)',
    'V9 — Day 43', 'V10 — Day 50', 'V11 — Day 57', 'V12 — End of Treatment',
  ],
  HERALD: [
    'V1 — Screening', 'V2 — Baseline', 'V3 — Week 1', 'V4 — Week 2',
    'V5 — Week 4', 'V6 — Week 8', 'V7 — Week 12', 'V8 — End of Treatment',
    'V9 — Follow-up 30d', 'V10 — Follow-up 90d',
  ],
};

/* ---------- Procedure → default unit mapping ---------- */
const PROCEDURES = [
  'Vital Signs — Heart Rate', 'Vital Signs — Blood Pressure (sys)',
  'Vital Signs — Blood Pressure (dia)', 'Vital Signs — Temperature',
  'Vital Signs — SpO2', 'Safety Blood Samples', 'CBC', 'ECG — QTcF',
  'Urinalysis', 'Pregnancy Test (urine)', 'PK Sample', 'Blood PD',
  'AE Assessment', 'Physical Exam',
] as const;

const UNIT_MAP: Record<string, string> = {
  'Vital Signs — Heart Rate': 'bpm',
  'Vital Signs — Blood Pressure (sys)': 'mmHg',
  'Vital Signs — Blood Pressure (dia)': 'mmHg',
  'Vital Signs — Temperature': '°C',
  'Vital Signs — SpO2': '%',
  'Safety Blood Samples': 'mL',
  CBC: '10*9/L',
  'ECG — QTcF': 'ms',
  Urinalysis: 'pH',
  'Pregnancy Test (urine)': 'pos/neg',
  'PK Sample': 'ng/mL',
  'Blood PD': 'pg/mL',
  'AE Assessment': 'CTCAE grade',
  'Physical Exam': 'normal/abnormal',
};

/* ---------- Shared styles ---------- */
const card: React.CSSProperties = {
  backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8, padding: '16px 20px',
};
const label: React.CSSProperties = {
  display: 'block', fontSize: 11, color: '#a0aec0', textTransform: 'uppercase',
  letterSpacing: 0.5, fontWeight: 600, marginBottom: 4,
};
const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13,
  backgroundColor: '#0d1117', border: '1px solid #2d3748', borderRadius: 6,
  color: '#e2e8f0', outline: 'none',
};
const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #2d3748',
  color: '#a0aec0', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
};
const td: React.CSSProperties = {
  padding: '10px 12px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 13,
};

/* ---------- Helpers ---------- */
function statusColor(s: string) {
  if (s === 'pass') return { color: '#48bb78', bg: '#22543d', label: 'PASS — Data accepted' };
  if (s === 'warning') return { color: '#ecc94b', bg: '#744210', label: 'WARNING — Review needed' };
  return { color: '#f56565', bg: '#742a2a', label: 'FAIL — Rejected' };
}

function statusBadge(s: string) {
  const c = statusColor(s);
  return (
    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: c.color, backgroundColor: c.bg }}>
      {s.toUpperCase()}
    </span>
  );
}

function nowLocal(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

/* ---------- Component ---------- */
export default function FHIRSimulator() {
  /* form state */
  const [patients, setPatients] = useState<DemoPatient[]>([]);
  const [patientId, setPatientId] = useState('');
  const [protocol, setProtocol] = useState('Sym004-09');
  const [visit, setVisit] = useState('');
  const [procedure, setProcedure] = useState<string>(PROCEDURES[0]);
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState(UNIT_MAP[PROCEDURES[0]]);
  const [timestamp, setTimestamp] = useState(nowLocal);

  /* result state */
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TriggerResult | null>(null);
  const [fhirOpen, setFhirOpen] = useState(false);
  const [history, setHistory] = useState<HistoryRow[]>([]);

  /* load patients + history */
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/fhir-sim/demo-patients`).then(r => r.json()).then((d) => {
      setPatients(d); if (d.length) setPatientId(d[0].id);
    }).catch(() => {});
    fetch(`${API_BASE}/api/v1/fhir-sim/history`).then(r => r.json()).then(setHistory).catch(() => {});
  }, []);

  /* auto-set visit when protocol changes */
  useEffect(() => {
    const v = PROTOCOL_VISITS[protocol];
    if (v?.length) setVisit(v[0]);
  }, [protocol]);

  /* auto-set unit when procedure changes */
  useEffect(() => { setUnit(UNIT_MAP[procedure] || ''); }, [procedure]);

  /* fire trigger */
  const fireTrigger = useCallback(async () => {
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/fhir-sim/trigger`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: patientId, procedure, value, unit, timestamp, visit_name: visit, protocol_id: protocol }),
      });
      const data: TriggerResult = await res.json();
      setResult(data);
      /* refresh history */
      fetch(`${API_BASE}/api/v1/fhir-sim/history`).then(r => r.json()).then(setHistory).catch(() => {});
    } catch { /* silent */ }
    setLoading(false);
  }, [patientId, procedure, value, unit, timestamp, visit, protocol]);

  const visits = PROTOCOL_VISITS[protocol] || [];

  /* ---------- Render ---------- */
  return (
    <div style={{ padding: 32 }}>
      {/* HEADER */}
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#e2e8f0', margin: 0 }}>
        Epic FHIR R4 Trigger Simulator
      </h1>
      <p style={{ fontSize: 13, color: '#718096', margin: '4px 0 0' }}>
        Simulates nurse entry in Epic EHR &rarr; FHIR R4 &rarr; D3RI ingestion &rarr; SDV validation
      </p>
      <ComplianceBadges badges={['FHIR R4', 'HL7', '21 CFR 11'] as any} />

      {/* MAIN PANELS */}
      <div style={{ display: 'flex', gap: 20, marginTop: 16 }}>
        {/* LEFT — Epic Nurse Entry */}
        <div style={{ flex: '0 0 45%' }}>
          <div style={{ ...card, borderTop: '3px solid #4299e1' }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#63b3ed', margin: '0 0 16px' }}>
              Epic Nurse Station (Simulated)
            </h2>

            {/* Patient */}
            <div style={{ marginBottom: 12 }}>
              <span style={label}>Patient</span>
              <select style={input} value={patientId} onChange={e => setPatientId(e.target.value)}>
                {patients.map(p => (
                  <option key={p.id} value={p.id}>{p.id} — {p.name} ({p.site})</option>
                ))}
              </select>
            </div>

            {/* Protocol */}
            <div style={{ marginBottom: 12 }}>
              <span style={label}>Protocol</span>
              <select style={input} value={protocol} onChange={e => setProtocol(e.target.value)}>
                <option value="Sym004-09">Sym004-09</option>
                <option value="HERALD">HERALD</option>
              </select>
            </div>

            {/* Visit */}
            <div style={{ marginBottom: 12 }}>
              <span style={label}>Visit</span>
              <select style={input} value={visit} onChange={e => setVisit(e.target.value)}>
                {visits.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            {/* Procedure */}
            <div style={{ marginBottom: 12 }}>
              <span style={label}>Procedure</span>
              <select style={input} value={procedure} onChange={e => setProcedure(e.target.value)}>
                {PROCEDURES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            {/* Value + Unit row */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <span style={label}>Value</span>
                <input style={input} value={value} onChange={e => setValue(e.target.value)} placeholder="e.g. 72" />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>Unit</span>
                <input style={input} value={unit} onChange={e => setUnit(e.target.value)} />
              </div>
            </div>

            {/* Timestamp */}
            <div style={{ marginBottom: 20 }}>
              <span style={label}>Timestamp</span>
              <input type="datetime-local" style={input} value={timestamp} onChange={e => setTimestamp(e.target.value)} />
            </div>

            {/* Fire button */}
            <button
              onClick={fireTrigger}
              disabled={loading || !value}
              style={{
                width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 700,
                backgroundColor: loading ? '#2d3748' : '#4299e1', color: '#fff',
                border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
              }}
            >
              {loading ? 'Sending FHIR R4 Observation...' : 'Fire FHIR Trigger'}
            </button>
          </div>
        </div>

        {/* RIGHT — SDV Result */}
        <div style={{ flex: '0 0 55%', minWidth: 0 }}>
          {!result && !loading && (
            <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320, color: '#718096', fontSize: 14 }}>
              Fire a FHIR trigger to see the D3RI SDV result here.
            </div>
          )}

          {loading && (
            <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320, color: '#63b3ed', fontSize: 14 }}>
              Processing FHIR R4 Observation...
            </div>
          )}

          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Status banner */}
              {(() => {
                const c = statusColor(result.sdv_result.match_status);
                return (
                  <div style={{ ...card, backgroundColor: c.bg, borderColor: c.color, textAlign: 'center' }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.label}</span>
                  </div>
                );
              })()}

              {/* FHIR Resource Preview */}
              <div style={card}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setFhirOpen(!fhirOpen)}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>FHIR R4 Observation Resource</span>
                  <span style={{ color: '#63b3ed', fontSize: 12 }}>{fhirOpen ? 'Collapse' : 'Expand'}</span>
                </div>
                {fhirOpen && (
                  <pre style={{
                    marginTop: 12, padding: 12, backgroundColor: '#0d1117', border: '1px solid #2d3748',
                    borderRadius: 6, fontSize: 11, color: '#63b3ed', overflowX: 'auto', maxHeight: 280,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {JSON.stringify(result.fhir_resource, null, 2)}
                  </pre>
                )}
              </div>

              {/* SDV Validation Results */}
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>SDV Validation</span>
                  {statusBadge(result.sdv_result.match_status)}
                </div>
                {result.sdv_result.deviations.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#48bb78', fontSize: 13 }}>
                    <span style={{ fontSize: 16 }}>&#10003;</span> All checks passed
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Field', 'Expected', 'Actual', 'Severity'].map(h => (
                          <th key={h} style={th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.sdv_result.deviations.map((d, i) => {
                        const sevColor = d.severity === 'critical' ? '#f56565' : d.severity === 'major' ? '#ed8936' : '#ecc94b';
                        return (
                          <tr key={i}>
                            <td style={td}>{d.field}</td>
                            <td style={td}>{d.expected}</td>
                            <td style={td}>{d.actual}</td>
                            <td style={{ ...td, color: sevColor, fontWeight: 600 }}>{d.severity}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Ingestion status */}
              <div style={card}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>Ingestion: </span>
                <span style={{
                  fontSize: 13,
                  color: result.ingestion_status === 'accepted' ? '#48bb78' : '#f56565',
                }}>
                  {result.ingestion_status === 'accepted'
                    ? 'Accepted into D3RI evidence graph'
                    : 'Rejected — manual review required'}
                </span>
                <span style={{ display: 'block', fontSize: 11, color: '#718096', marginTop: 4 }}>
                  Observation ID: {result.observation_id}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM — Simulation History */}
      {history.length > 0 && (
        <div style={{ ...card, marginTop: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0', margin: '0 0 12px' }}>Simulation History</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Time', 'Patient', 'Procedure', 'Value', 'Status', 'Observation ID'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((row, i) => (
                  <tr key={i}>
                    <td style={td}>{new Date(row.time).toLocaleString()}</td>
                    <td style={td}>{row.patient}</td>
                    <td style={td}>{row.procedure}</td>
                    <td style={td}>{row.value}</td>
                    <td style={td}>{statusBadge(row.status)}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{row.observation_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
